/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { HoverCard } from '../../hover-card/HoverCard';
import { SkillHoverCardContent } from '../skill-hover-card-content/SkillHoverCardContent';
import { skillIconMap } from '../skill-icons';
import type { Skill } from '../types';
import { SkillTagInternal } from './SkillTagInternal';

export type RovoSkillTagProps = Omit<Skill, 'id'> & {
	// invert colors for use with dark backgrounds
	invertColors?: boolean;
	// disable hovercard functionality
	disableHovercard?: boolean;
	// show shimmer effect
	shimmer?: boolean;
	// optional max-height applied to the hover card content container
	hovercardMaxHeight?: number | string;
};

// delay opening and closing of the hovercard to prevent flickering
const SKILL_TAG_HOVERCARD_DELAY = 200;

export const SkillTag = ({
	invertColors = false,
	disableHovercard = false,
	product,
	iconName,
	hovercardMaxHeight,
	...props
}: RovoSkillTagProps): JSX.Element => {
	const IconComponent =
		iconName && !fg('skills_fe_display_slash_command') ? skillIconMap[iconName] : undefined;

	const hasHovercardInfo = props?.name && props?.description;
	const shouldWrapLabel = fg('rovo_skill_tag_label_wrapping');

	return disableHovercard || !hasHovercardInfo ? (
		<SkillTagInternal
			{...props}
			invertColors={invertColors}
			disableHovercard={disableHovercard}
			IconComponent={IconComponent}
			shouldWrapLabel={shouldWrapLabel}
		/>
	) : (
		<HoverCard
			placement="top"
			fallbackPlacements={['bottom', 'left']}
			autoFocus={false}
			hoverCardDelay={SKILL_TAG_HOVERCARD_DELAY}
			shouldRenderToParent={false}
			shouldRenderTriggerInline={shouldWrapLabel}
			content={
				<SkillHoverCardContent
					product={product}
					iconName={iconName}
					maxHeight={hovercardMaxHeight}
					{...props}
				/>
			}
		>
			<SkillTagInternal
				invertColors={invertColors}
				disableHovercard={disableHovercard}
				IconComponent={IconComponent}
				shouldWrapLabel={shouldWrapLabel}
				{...props}
			/>
		</HoverCard>
	);
};
